import * as THREE from 'three';
import { DROP_DESCENT_TIME, DROP_LIFETIME } from './config';
import type { Drop, DropType } from './types';

const DROP_COLORS: Record<DropType, number> = {
  medkit: 0xef6d67,
  shield: 0x65edf2,
  baseShield: 0xb889f2,
  credits: 0xf49a55,
};

interface DropSystemDependencies {
  scene: THREE.Scene;
  getPlayerPosition: () => THREE.Vector3;
  onCollect: (drop: Drop) => void;
}

/** Runtime presentation for drops. The authoritative pickup result stays in GameState. */
export class DropSystem {
  private readonly drops: Drop[] = [];
  private nextId = 1;

  constructor(private readonly deps: DropSystemDependencies) {}

  get active(): readonly Drop[] { return this.drops; }

  spawn(type: DropType, target: THREE.Vector3): Drop {
    const landingMarker = createLandingMarker(type);
    const drop: Drop = {
      id: this.nextId++,
      type,
      mesh: createDropMesh(type),
      landingMarker,
      target: target.clone(),
      landed: false,
      descent: 0,
      life: DROP_LIFETIME,
    };
    drop.mesh.position.set(target.x, 10.5, target.z);
    landingMarker.position.set(target.x, 0.045, target.z);
    this.deps.scene.add(landingMarker, drop.mesh);
    this.drops.push(drop);
    return drop;
  }

  update(dt: number): void {
    const playerPosition = this.deps.getPlayerPosition();
    for (let index = this.drops.length - 1; index >= 0; index--) {
      const drop = this.drops[index];
      if (!drop.landed) {
        drop.descent = Math.min(1, drop.descent + dt / DROP_DESCENT_TIME);
        const eased = 1 - Math.pow(1 - drop.descent, 2);
        drop.mesh.position.y = THREE.MathUtils.lerp(10.5, 0.1, eased);
        drop.mesh.rotation.y += dt * 0.55;
        if (drop.landingMarker) {
          drop.landingMarker.rotation.y -= dt * 0.8;
          const pulse = 1 + Math.sin(drop.descent * Math.PI * 8) * 0.08;
          drop.landingMarker.scale.setScalar(pulse);
        }
        const canopy = drop.mesh.getObjectByName('drop-canopy');
        if (canopy) canopy.position.y = 1.16 + Math.sin(drop.descent * Math.PI * 7) * 0.04;
        if (drop.descent >= 1) {
          drop.landed = true;
          this.removeParachute(drop.mesh);
          this.removeLandingMarker(drop);
          drop.mesh.rotation.y = 0;
          drop.mesh.scale.setScalar(1.24);
        }
      } else {
        drop.life -= dt;
        const age = DROP_LIFETIME - drop.life;
        drop.mesh.position.y = 0.1 + Math.sin(age * 3) * 0.035;
        drop.mesh.rotation.y += dt * 0.62;
        const urgency = drop.life < 6 ? 0.055 : 0.022;
        drop.mesh.scale.setScalar(1.15 + Math.sin(age * (drop.life < 6 ? 9 : 3)) * urgency);
        drop.mesh.visible = drop.life >= 4 || Math.sin(age * 15) > -0.25;
        if (drop.mesh.position.distanceTo(playerPosition) < 1.2) {
          this.deps.onCollect(drop);
          this.remove(index);
          continue;
        }
        if (drop.life <= 0) {
          this.remove(index);
          continue;
        }
      }
    }
  }

  clear(): void {
    for (let index = this.drops.length - 1; index >= 0; index--) this.remove(index);
  }

  private remove(index: number): void {
    const drop = this.drops[index];
    if (!drop) return;
    this.deps.scene.remove(drop.mesh);
    this.removeLandingMarker(drop);
    disposeRenderableTree(drop.mesh);
    this.drops.splice(index, 1);
  }

  private removeParachute(dropMesh: THREE.Group): void {
    const parachute = dropMesh.getObjectByName('drop-parachute');
    if (!parachute) return;
    dropMesh.remove(parachute);
    disposeRenderableTree(parachute);
  }

  private removeLandingMarker(drop: Drop): void {
    if (!drop.landingMarker) return;
    this.deps.scene.remove(drop.landingMarker);
    disposeRenderableTree(drop.landingMarker);
    drop.landingMarker = undefined;
  }
}

function createDropMesh(type: DropType): THREE.Group {
  const color = DROP_COLORS[type];
  const group = new THREE.Group();
  group.name = `drop-${type}`;
  createDropModel(group, type, color);

  const parachute = new THREE.Group();
  parachute.name = 'drop-parachute';
  const canopyMaterial = new THREE.MeshStandardMaterial({ color, transparent: true, opacity: 0.78, side: THREE.DoubleSide, emissive: color, emissiveIntensity: 0.16 });
  const canopy = new THREE.Mesh(new THREE.SphereGeometry(0.82, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), canopyMaterial);
  canopy.name = 'drop-canopy';
  canopy.position.y = 1.16;
  canopy.castShadow = true;
  parachute.add(canopy);
  const ropes = new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-0.65, 1.12, -0.35), new THREE.Vector3(-0.3, 0.55, -0.3),
      new THREE.Vector3(0.65, 1.12, -0.35), new THREE.Vector3(0.3, 0.55, -0.3),
      new THREE.Vector3(-0.65, 1.12, 0.35), new THREE.Vector3(-0.3, 0.55, 0.3),
      new THREE.Vector3(0.65, 1.12, 0.35), new THREE.Vector3(0.3, 0.55, 0.3),
    ]),
    new THREE.LineBasicMaterial({ color: 0xc5d8d2, transparent: true, opacity: 0.68 }),
  );
  parachute.add(ropes);
  group.add(parachute);
  const glow = new THREE.PointLight(color, 2.5, 3.5);
  glow.position.y = 0.68;
  group.add(glow);
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.68, 0.74, 8), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.72, side: THREE.DoubleSide }));
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.06;
  group.add(ring);
  return group;
}

function createLandingMarker(type: DropType): THREE.Group {
  const color = DROP_COLORS[type];
  const group = new THREE.Group();
  group.name = `drop-landing-marker-${type}`;

  const outer = new THREE.Mesh(
    new THREE.RingGeometry(0.72, 0.82, 24),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.76, side: THREE.DoubleSide, depthWrite: false }),
  );
  outer.rotation.x = -Math.PI / 2;
  group.add(outer);

  const inner = new THREE.Mesh(
    new THREE.RingGeometry(0.18, 0.23, 16),
    new THREE.MeshBasicMaterial({ color: 0xf1fbff, transparent: true, opacity: 0.88, side: THREE.DoubleSide, depthWrite: false }),
  );
  inner.rotation.x = -Math.PI / 2;
  inner.position.y = 0.008;
  group.add(inner);

  const bracketMaterial = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.92, depthWrite: false });
  for (let index = 0; index < 4; index++) {
    const angle = index * Math.PI / 2;
    const bracket = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.018, 0.055), bracketMaterial);
    bracket.position.set(Math.cos(angle) * 0.52, 0.014, Math.sin(angle) * 0.52);
    bracket.rotation.y = -angle;
    group.add(bracket);
  }
  return group;
}

function createDropModel(group: THREE.Group, type: DropType, color: number): void {
  if (type === 'medkit') {
    createMedkitModel(group);
    return;
  }
  if (type === 'shield' || type === 'baseShield') {
    createShieldModel(group, color, type === 'baseShield');
    return;
  }
  createCreditsModel(group, color);
}

function createMedkitModel(group: THREE.Group): void {
  const caseMaterial = new THREE.MeshStandardMaterial({
    color: 0xf0f3e8,
    emissive: 0x343a31,
    emissiveIntensity: 0.18,
    metalness: 0.18,
    roughness: 0.5,
  });
  const redMaterial = new THREE.MeshStandardMaterial({
    color: 0xef4e52,
    emissive: 0x8f1f29,
    emissiveIntensity: 0.45,
    metalness: 0.2,
    roughness: 0.38,
  });
  const darkMaterial = new THREE.MeshStandardMaterial({
    color: 0x25343a,
    emissive: 0x101b1e,
    emissiveIntensity: 0.25,
    metalness: 0.55,
    roughness: 0.42,
  });

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.34, 0.7), caseMaterial);
  body.position.y = 0.3;
  body.castShadow = true;
  group.add(body);

  const lid = new THREE.Mesh(new THREE.BoxGeometry(0.96, 0.1, 0.74), caseMaterial);
  lid.position.y = 0.52;
  lid.castShadow = true;
  group.add(lid);

  const frontTrim = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.07, 0.06), redMaterial);
  frontTrim.position.set(0, 0.39, -0.38);
  group.add(frontTrim);

  const handlePosts = [-0.14, 0.14].map((x) => {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.2, 0.08), darkMaterial);
    post.position.set(x, 0.66, 0.02);
    return post;
  });
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.07, 0.08), darkMaterial);
  handle.position.set(0, 0.75, 0.02);
  group.add(...handlePosts, handle);

  // The cross is deliberately raised on the lid so it remains legible in the tactical camera.
  const crossVertical = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.055, 0.5), redMaterial);
  const crossHorizontal = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.055, 0.16), redMaterial);
  crossVertical.position.y = crossHorizontal.position.y = 0.6;
  group.add(crossVertical, crossHorizontal);

  const latch = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.14, 0.07), redMaterial);
  latch.position.set(0, 0.49, -0.4);
  group.add(latch);
}

function createShieldModel(group: THREE.Group, color: number, isBaseShield: boolean): void {
  const scale = isBaseShield ? 1.08 : 0.9;
  const darkColor = isBaseShield ? 0x271d3b : 0x102e3a;
  const plateMaterial = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: isBaseShield ? 0.42 : 0.34,
    metalness: 0.48,
    roughness: 0.28,
    side: THREE.DoubleSide,
  });
  const innerMaterial = new THREE.MeshStandardMaterial({
    color: darkColor,
    emissive: color,
    emissiveIntensity: 0.22,
    metalness: 0.55,
    roughness: 0.32,
    side: THREE.DoubleSide,
  });
  const coreMaterial = new THREE.MeshStandardMaterial({
    color: 0xeafcff,
    emissive: color,
    emissiveIntensity: 1.2,
    metalness: 0.15,
    roughness: 0.2,
  });

  const pedestal = new THREE.Mesh(
    new THREE.CylinderGeometry(0.48 * scale, 0.58 * scale, 0.16, 8),
    innerMaterial,
  );
  pedestal.position.y = 0.16;
  pedestal.castShadow = true;
  group.add(pedestal);

  const shieldGeometry = createShieldGeometry(scale);
  const shield = new THREE.Mesh(shieldGeometry, plateMaterial);
  shield.name = 'drop-shield-plate';
  shield.position.set(0, 0.82, -0.02);
  shield.castShadow = true;
  group.add(shield);

  const innerShield = new THREE.Mesh(createShieldGeometry(scale * 0.72), innerMaterial);
  innerShield.position.set(0, 0.82, 0.055);
  group.add(innerShield);

  const outline = new THREE.LineSegments(
    new THREE.EdgesGeometry(shieldGeometry),
    new THREE.LineBasicMaterial({ color: 0xeafcff, transparent: true, opacity: 0.9 }),
  );
  outline.position.copy(shield.position);
  outline.position.z = 0.06;
  group.add(outline);

  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.13 * scale, 1), coreMaterial);
  core.position.set(0, 0.82, 0.13);
  core.castShadow = true;
  group.add(core);

  const centerBar = new THREE.Mesh(
    new THREE.BoxGeometry(0.055 * scale, 0.52 * scale, 0.055),
    new THREE.MeshBasicMaterial({ color: 0xeafcff, transparent: true, opacity: 0.82 }),
  );
  centerBar.position.set(0, 0.82, 0.14);
  group.add(centerBar);

  if (isBaseShield) {
    const nodeMaterial = new THREE.MeshBasicMaterial({ color: 0xeacbff });
    const nodePositions: Array<[number, number]> = [[-0.42, 0.51], [0.42, 0.51], [-0.33, -0.28], [0.33, -0.28]];
    nodePositions.forEach(([x, y]) => {
      const node = new THREE.Mesh(new THREE.OctahedronGeometry(0.075, 0), nodeMaterial);
      node.position.set(x * scale, 0.82 + y * scale, 0.12);
      group.add(node);
    });
  }
}

function createShieldGeometry(scale: number): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0.6 * scale);
  shape.lineTo(0.34 * scale, 0.39 * scale);
  shape.lineTo(0.29 * scale, -0.17 * scale);
  shape.lineTo(0, -0.53 * scale);
  shape.lineTo(-0.29 * scale, -0.17 * scale);
  shape.lineTo(-0.34 * scale, 0.39 * scale);
  shape.closePath();
  return new THREE.ExtrudeGeometry(shape, { depth: 0.1, bevelEnabled: true, bevelSegments: 2, steps: 1, bevelSize: 0.025, bevelThickness: 0.025 });
}

function createCreditsModel(group: THREE.Group, color: number): void {
  const goldMaterial = new THREE.MeshStandardMaterial({
    color,
    emissive: 0x8b3c16,
    emissiveIntensity: 0.38,
    metalness: 0.82,
    roughness: 0.24,
  });
  const edgeMaterial = new THREE.MeshStandardMaterial({
    color: 0xffc878,
    emissive: color,
    emissiveIntensity: 0.45,
    metalness: 0.88,
    roughness: 0.2,
  });

  for (let index = 0; index < 3; index++) {
    const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.39, 0.39, 0.13, 12), goldMaterial);
    coin.position.y = 0.2 + index * 0.14;
    coin.rotation.y = index * 0.18;
    coin.castShadow = true;
    group.add(coin);
  }
  const topCoin = new THREE.Mesh(new THREE.CylinderGeometry(0.41, 0.41, 0.055, 12), edgeMaterial);
  topCoin.position.y = 0.64;
  topCoin.castShadow = true;
  group.add(topCoin);

  const coinMark = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.035, 6, 12), new THREE.MeshBasicMaterial({ color: 0xffe1a5 }));
  coinMark.rotation.x = Math.PI / 2;
  coinMark.position.y = 0.68;
  group.add(coinMark);
}

function disposeRenderableTree(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) && !(object instanceof THREE.Sprite) && !(object instanceof THREE.Line)) return;
    object.geometry.dispose();
    const material = object.material;
    (Array.isArray(material) ? material : [material]).forEach((item) => item.dispose());
  });
}
